// Version: 1.3
// v1.3: Inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v1.2: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v1.1: hub_schedules and hub_resources added.
// Admin page for managing subscription tier definitions.
// Lives at /subscription-tiers in the admin nav.
// Tiers are stored in portal/data/subscription_tiers.json on the NAS.
// Changes here affect the tier selector in all ClientRow Subscription tabs.

import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from './auth';

const ALL_HUBS = [
  'hub_cyber',
  'hub_device',
  'hub_compliance',
  'hub_digital',
  'hub_productivity',
  'hub_assets',
  'hub_schedules',
  'hub_resources',
];

const HUB_LABELS = {
  hub_cyber:        'Cyber Security Hub',
  hub_device:       'Device Hub',
  hub_compliance:   'Compliance Hub',
  hub_digital:      'Digital Hub',
  hub_productivity: 'Productivity Hub',
  hub_assets:       'Assets Hub',
  hub_schedules:    'Schedules',
  hub_resources:    'Resources',
};

const HUB_DESCS = {
  hub_cyber:        'Assessments, email security, UK Cyber Alerts',
  hub_device:       'Clearview agent, endpoint monitoring, device cards',
  hub_compliance:   'CISO Assistant, CE+ frameworks, backup status',
  hub_digital:      'Domains, certificates, PageSpeed, SEO and Geo tracking',
  hub_productivity: 'Microsoft 365, Secure Score, MFA adoption',
  hub_assets:       'Asset register, subscriptions and contract management',
  hub_schedules:    'Automated report and scan scheduling for clients',
  hub_resources:    'NCSC alerts feed, guidance notes and policy templates',
};

const PRESET_COLOURS = ['#5856d6', '#30d158', '#00d4ff', '#bf5af2', '#e6b800', '#ff2d55', '#ff9500', '#0a84ff'];

function newTier() {
  return {
    id:          `tier_${Date.now()}`,
    name:        '',
    description: '',
    colour:      '#5856d6',
    hubs:        Object.fromEntries(ALL_HUBS.map(h => [h, false])),
  };
}

export default function SubscriptionTiers() {
  const [tiers,   setTiers]   = useState([]);
  const [dirty,   setDirty]   = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // tier id being edited

  useEffect(() => {
    authFetch('/api/admin/subscription-tiers')
      .then(r => r.json())
      .then(d => { setTiers(d.tiers || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch('/api/admin/subscription-tiers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiers }),
      });
      const d = await res.json();
      setTiers(d.tiers || tiers);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {}
    setSaving(false);
  };

  const updateTier = (id, patch) => {
    setTiers(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
    setDirty(true);
  };

  const toggleHub = (tierId, hub) => {
    setTiers(prev => prev.map(t => {
      if (t.id !== tierId) return t;
      return { ...t, hubs: { ...t.hubs, [hub]: !t.hubs[hub] } };
    }));
    setDirty(true);
  };

  const addTier = () => {
    const t = newTier();
    setTiers(prev => [...prev, t]);
    setEditing(t.id);
    setDirty(true);
  };

  const deleteTier = (id) => {
    if (!window.confirm('Delete this tier? Clients currently using it will keep their feature flags but the tier label will no longer appear.')) return;
    setTiers(prev => prev.filter(t => t.id !== id));
    if (editing === id) setEditing(null);
    setDirty(true);
  };

  const moveTier = (id, dir) => {
    setTiers(prev => {
      const arr = [...prev];
      const idx = arr.findIndex(t => t.id === id);
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return prev;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
    setDirty(true);
  };

  if (loading) return (
    <div className="page-content" style={{ color: 'var(--text-muted)', padding: 40 }}>Loading…</div>
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-title">Subscription Tiers</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
          Define the subscription tiers available for client accounts. Tiers set which hubs are included by default.
          Admins can override individual flags per client in the Subscription tab.
        </div>
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <button className="btn btn-primary"
          onClick={addTier}
         
        >
          <i className="ti ti-plus" /> Add tier
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {saved && <span style={{ fontSize: 12, color: '#30d158', fontWeight: 600 }}>✓ Saved</span>}
          {dirty && !saved && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Unsaved changes</span>}
          <button className="btn btn-primary" style={{ minWidth: 100, opacity: dirty ? 1 : 0.4 }}
            onClick={save}
            disabled={saving || !dirty}
           
           
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {tiers.length === 0 && (
        <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
          No tiers defined yet. Click "Add tier" to create your first one.
        </div>
      )}

      {/* Tier list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {tiers.map((tier, idx) => {
          const isEditing = editing === tier.id;
          const hubCount  = ALL_HUBS.filter(h => tier.hubs[h]).length;

          return (
            <div
              key={tier.id}
              style={{
                background: 'var(--surface)',
                border: `1px solid ${isEditing ? tier.colour : 'var(--border)'}`,
                borderRadius: 10,
                overflow: 'hidden',
                transition: 'border-color 0.15s',
              }}
            >
              {/* Tier header row */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer' }}
                onClick={() => setEditing(isEditing ? null : tier.id)}
              >
                {/* Colour swatch */}
                <div style={{
                  width: 14, height: 14, borderRadius: '50%',
                  background: tier.colour, flexShrink: 0,
                  boxShadow: `0 0 0 3px ${tier.colour}33`,
                }} />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>
                    {tier.name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Unnamed tier</span>}
                  </div>
                  {tier.description && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{tier.description}</div>
                  )}
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                    {ALL_HUBS.filter(h => tier.hubs[h]).map(h => (
                      <span key={h} style={{
                        fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                        background: `${tier.colour}20`, color: tier.colour,
                        border: `1px solid ${tier.colour}33`,
                      }}>
                        {HUB_LABELS[h].replace(' Hub', '').replace(' Security', '')}
                      </span>
                    ))}
                    {hubCount === 0 && (
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>No hubs selected</span>
                    )}
                  </div>
                </div>

                {/* Order controls */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button className="btn btn-quiet btn-sm" style={{ opacity: idx === 0 ? 0.3 : 1 }}
                    onClick={e => { e.stopPropagation(); moveTier(tier.id, -1); }}
                    disabled={idx === 0}
                   
                  ><i className="ti ti-chevron-up" /></button>
                  <button className="btn btn-quiet btn-sm" style={{ opacity: idx === tiers.length - 1 ? 0.3 : 1 }}
                    onClick={e => { e.stopPropagation(); moveTier(tier.id, 1); }}
                    disabled={idx === tiers.length - 1}
                   
                  ><i className="ti ti-chevron-down" /></button>
                </div>

                <button className="btn btn-quiet btn-sm" style={{ flexShrink: 0 }}
                  onClick={e => { e.stopPropagation(); deleteTier(tier.id); }}
                 
                  title="Delete tier"
                ><i className="ti ti-trash" /></button>

                <i className={`ti ${isEditing ? 'ti-chevron-up' : 'ti-chevron-down'}`} style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }} />
              </div>

              {/* Edit panel */}
              {isEditing && (
                <div style={{ padding: '0 16px 18px', borderTop: '1px solid var(--border)', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>

                  {/* Name and description */}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 160px' }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Tier name</label>
                      <input className="input-sm"
                        value={tier.name}
                        onChange={e => updateTier(tier.id, { name: e.target.value })}
                        placeholder="e.g. Professional"
                        style={{ width: '100%' }}
                        onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                        onBlur={e => e.target.style.borderColor = 'var(--border)'}
                      />
                    </div>
                    <div style={{ flex: '2 1 240px' }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Description</label>
                      <input className="input-sm"
                        value={tier.description}
                        onChange={e => updateTier(tier.id, { description: e.target.value })}
                        placeholder="Short description shown to admins"
                        style={{ width: '100%' }}
                        onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                        onBlur={e => e.target.style.borderColor = 'var(--border)'}
                      />
                    </div>
                  </div>

                  {/* Colour picker */}
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>Pill colour</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {PRESET_COLOURS.map(col => (
                        <div
                          key={col}
                          onClick={() => updateTier(tier.id, { colour: col })}
                          style={{
                            width: 24, height: 24, borderRadius: '50%', background: col, cursor: 'pointer',
                            boxShadow: tier.colour === col ? `0 0 0 2px var(--bg), 0 0 0 4px ${col}` : 'none',
                            transition: 'box-shadow 0.15s',
                          }}
                        />
                      ))}
                      <input
                        type="color"
                        value={tier.colour}
                        onChange={e => updateTier(tier.id, { colour: e.target.value })}
                        title="Custom colour"
                        style={{
                          width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--border)',
                          background: 'var(--bg)', cursor: 'pointer', padding: 2,
                        }}
                      />
                    </div>
                  </div>

                  {/* Hub toggles */}
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 10 }}>Hubs included in this tier</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {ALL_HUBS.map(hub => {
                        const on = !!tier.hubs[hub];
                        return (
                          <div
                            key={hub}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12,
                              padding: '10px 14px',
                              background: on ? 'var(--surface)' : 'rgba(255,255,255,0.02)',
                              border: `1px solid ${on ? 'var(--border)' : 'rgba(255,255,255,0.06)'}`,
                              borderRadius: 8,
                            }}
                          >
                            <div
                              onClick={() => toggleHub(tier.id, hub)}
                              style={{
                                width: 34, height: 20, borderRadius: 10,
                                background: on ? tier.colour : 'rgba(255,255,255,0.12)',
                                position: 'relative', cursor: 'pointer', flexShrink: 0,
                                transition: 'background 0.2s',
                              }}
                            >
                              <div style={{
                                position: 'absolute', top: 2,
                                left: on ? 16 : 2,
                                width: 16, height: 16, borderRadius: 8,
                                background: '#fff', transition: 'left 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                              }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: on ? 'var(--text)' : 'var(--text-muted)' }}>
                                {HUB_LABELS[hub]}
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                                {HUB_DESCS[hub]}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom save */}
      {tiers.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-primary" style={{ minWidth: 120, opacity: dirty ? 1 : 0.4 }}
            onClick={save}
            disabled={saving || !dirty}
           
           
          >
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save changes'}
          </button>
        </div>
      )}
    </div>
  );
}
